import { AxiosError, AxiosResponse } from 'axios';
import { Observable, catchError, from, map, switchMap, tap } from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';

import { setEntities } from '@ngneat/elf-entities';
import { TableFilter } from '@store/common/tableFilter.model';
import { filesService } from '@store/files';
import { extractFilenameFromResponse } from '@utils/response.utils';
import {
  AuditOperation,
  AuditOperationFilters,
  AuditOperationStepTypeEnum,
  CreateAuditOperation,
  SearchAuditStepOperationDto
} from './audits-operation.model';
import { getAuditsOperationDataSource } from './audits-operation.requests';
import { auditsOperationStore } from './audits-operation.store';

export class AuditsOperationService {
  store = auditsOperationStore;

    getStep = () => {
        const { step } = this.store.getValue();
        return step;
    };
    setStep = (step: AuditOperationStepTypeEnum) =>
        this.store.update((state) => ({
            ...state,
            step: step
        }));

  resetStore = () => this.store.reset();
  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: { search: '', tableFilters: AuditOperationFilters.initTableFilters() },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getAuditsInformationOP = (auditId: number | string): Observable<AuditOperation[]> => {
    const {filters: { tableFilters }} = this.store.getValue();

    const requestFilters = TableFilter.toRequestFilters(tableFilters);
      const searchDto: SearchAuditStepOperationDto = {
        step: this.getStep()
      };

      return from(
      APIAxios({
        ...APIRoutes.GetAuditsOperation(auditId),
          params: {
            ...requestFilters,
            stepType: searchDto.step,
          }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<AuditOperation[]>) => {
        return response.data;
      }),
      tap((auditsInformationOP) => {
        this.store.update(
          setEntities(auditsInformationOP),
          getAuditsOperationDataSource.setSuccess()
        );
      }),
    );
  };

  createAuditOperation = (data: CreateAuditOperation) => {
    return from(APIAxios({
      ...APIRoutes.POSTAuditsOperation(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOperation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformationOP(data.auditId!)),
      );
  };

  updateAuditOperation = (auditOperationId: number | string, data: CreateAuditOperation) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsOperation(auditOperationId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOperation>) => {
          return response.data;
        }),
        switchMap(() => this.getAuditsInformationOP(data.auditId!)),
      );
  };

    optimizeAuditOperation = (auditOperationId: number | string, data: CreateAuditOperation) => {
        return from(APIAxios({
            ...APIRoutes.PATCHOptimizeAuditsOperation(auditOperationId),
            data,
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<AuditOperation>) => {
                    return response.data;
                }),
                switchMap(() => this.getAuditsInformationOP(data.auditId!)),
            );
    };

    validateAuditOperation = (auditOperationId: number | string, auditId: number) => {
        return from(APIAxios({
            ...APIRoutes.PATCHValidateAuditsOperation(auditOperationId),
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<AuditOperation>) => {
                    return response.data;
                }),
                switchMap(() => this.getAuditsInformationOP(auditId)),
            );
    };


    deleteAuditOperation = (auditOperationId: number | string, auditId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEAuditsOperation(auditOperationId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getAuditsInformationOP(auditId)),
      );
  };

    getAuditCSV = (auditId: string | number) => {
        return from(
            APIAxios({
                ...APIRoutes.GETAuditCSV(auditId)
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            tap((response: AxiosResponse<Blob>) => {
                const filename = extractFilenameFromResponse(response);
                filesService.downloadDocument(response.data, filename || 'export.xlsx');
            }),
        );
    };

    getPrecoAuditCSV = (auditId: string | number) => {
        return from(
            APIAxios({
                ...APIRoutes.GETPrecoAuditCSV(auditId)
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            tap((response: AxiosResponse<Blob>) => {
                const filename = extractFilenameFromResponse(response);
                filesService.downloadDocument(response.data, filename || 'export.xlsx');
            }),
        );
    };

  getAuditOperation = (auditOperationId: number | string): Observable<AuditOperation> => {
    return from(APIAxios(APIRoutes.GetAuditOperationDetails(auditOperationId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOperation>) => {
          return response.data;
        }),
      );
  };

}

export const auditsOperationService = new AuditsOperationService();
